<template>
  <div>

    <loading-indicator
      v-if="isLoading"
    />

    <p v-if="canShowDeletedByRequestView">
      The player was deleted by request.<br>
      The player does not exist anymore.
    </p>

    <p v-if="canShowDeletedByInsiderUserView">
      Insider user deleted the player.<br>
      The player does not exist anymore.
    </p>

    <template v-if="canShowExistingPlayerView">
      <table class="prop-table mb-4">

        <template v-if="info.key_points">
          <tr>
            <th>Имя</th>
            <td :class="getNameClass">
              {{name | getOptionalStringDefaultStr('unknown')}}
            </td>
          </tr>
          <tr>
            <th>
              <span v-b-popover.hover.top="'Уровень, доступный игроку. Игрок еще не прошел этот уровень.'">Уровень</span>
            </th>
            <td>
              {{ info.key_points.Level | getFormattedNumber }}
              <template v-if="mayEditPlayerLevel">
                (<router-link :to="{name: 'player_level_edit', params: {playerId: playerId}}">изменить</router-link>)
              </template>
            </td>
          </tr>
          <tr>
            <th>Монеты</th>
            <td>{{info.key_points.Coins | getFormattedNumber}}</td>
          </tr>
        </template>

        <tr class="active-quests">
          <th class="align-top">Активные квесты</th>
          <td>
            <span v-if="!hasActiveQuests">
              Нет активных квестов.
            </span>

            <quest-proto-name-list
              v-if="hasActiveQuests"
              :questProtoIds="activeQuestProtoIds"
            />
          </td>
        </tr>

        <tr>
          <th>Facebook id</th>
          <td>{{info.facebookId | getOptionalStr}}</td>
        </tr>
        <tr>
          <th>Advertising id</th>
          <td>{{info.advertisingId | getAdvertisingIdStr}}</td>
        </tr>
        <tr>
          <th>NStats id</th>
          <td>{{info.nstatsId | getOptionalNumberStr('unknown')}}</td>
        </tr>
        <tr>
          <th>Reg. time</th>
          <td>{{info.reg_time | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Client update</th>
          <td>{{info.clientUpdateTime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Last login time</th>
          <td>{{info.lastLoginTime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Last online</th>
          <td>{{info.last_online_stamp | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Client</th>
          <td>{{info.client_version}}</td>
        </tr>
        <tr>
          <th>Configs version</th>
          <td>{{info.configsVersion | getOptionalStringStr}}</td>
        </tr>
        <tr>
          <th>Lang</th>
          <td>{{info.lang | getLangNameEn}}</td>
        </tr>
        <tr>
          <th>Country</th>
          <td>{{info.country | getOptionalStringStr}}</td>
        </tr>
        <tr>
          <th>Currency</th>
          <td>{{info.currency | getOptionalStringStr}}</td>
        </tr>
        <tr>
          <th>RFM category</th>
          <td>{{info.rfmCategory | getPlayerRFMCategoryNameShort}}</td>
        </tr>
        <tr>
          <th>Platform</th>
          <td>{{info.shard_player.Platform | getPlatformName}}</td>
        </tr>
        <tr>
          <th>DB Shard</th>
          <td>{{info.shard_player.ShardId}}</td>
        </tr>
        <tr>
          <th>Banned</th>
          <td :class="banClass">{{banStr}}</td>
        </tr>
        <tr>
          <th>Club</th>
          <td>
            <template v-if="info.club !== null">
              <club-link :clubId="info.club.id" />
              <span v-if="info.club.isOwner">(leader)</span>
            </template>
            <span v-if="info.club === null">
              Not a club member.
            </span>
          </td>
        </tr>
        <tr v-if="info.chatStatusInfo !== null">
          <th>Club chat</th>
          <td :class="clubChatStatusClass">
            {{clubChatStatusStr}}
          </td>
        </tr>
        <tr v-if="info.suspicionsMask !== null">
          <th>Suspicious activity</th>
          <td :class="suspiciousActivityClass">
            {{playerSuspiciousActivityStr}}
          </td>
        </tr>
        <tr>
          <th>Events</th>
          <td>
            <span :class="eventAccessClass">{{eventAccessStatusStr}}</span>
            <template v-if="mayManage">
              (<router-link :to="{name: 'player_event_access_status_edit', params: {playerId: playerId}}">edit</router-link>)
            </template>
          </td>
        </tr>
      </table>

      <h4>Contests</h4>
      <table class="prop-table mb-4">
        <tr>
          <th>Assembly</th>
          <td>{{info.leagueId | getEnumTmntIndividualLeagueName}}</td>
        </tr>
      </table>

      <h4>Last save</h4>
      <table v-if="info.dataVersion !== null" class="prop-table mb-4">
        <tr>
          <th>Time</th>
          <td>{{info.dataVersion.mtime | getMomentStr}}</td>
        </tr>
        <tr>
          <th>Ext id</th>
          <td>
            <span class="custom-code">{{info.dataVersion.ext_id}}</span>
          </td>
        </tr>
      </table>
      <p v-if="info.dataVersion === null" class="mb-4">
        No info about last save.
      </p>

      <div>
        <b-button @click="downloadState">Download state</b-button>
      </div>
    </template>

 </div>
</template>

<script>
import auth from '../auth.js';
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';
import LoadingIndicator from './LoadingIndicator.vue';
import QuestProtoNameList from './QuestProtoNameList.vue';
import ClubLink from './ClubLink.vue';

/**
 * @typedef { import("../model/player.js").PlayerInfo } PlayerInfo
 */

import { getPlayerName                              } from '../model/player.js';
import { playerHasName                              } from '../model/player.js';
import { mayEditPlayerLevel as doMayEditPlayerLevel } from '../model/player.js';
import { isPlayerExist                              } from '../model/player.js';
import { isPlayerDeletedByRequest                   } from '../model/player.js';
import { isPlayerDeletedByInsiderUser               } from '../model/player.js';
import { getPlayerSuspiciousActivityStr             } from '../model/player.js';
import { getEventAccessStatusName                   } from '../model/event_access.js';
import { getEventAccessStatusClass                  } from '../model/event_access.js';

import { getMomentMinStr } from '../util.js';
import { mayManage       } from '../util.js';

import { getEnumPlayerRFMCategoryName    } from '../autogen/bundle.js';
import { getEnumTmntIndividualLeagueName } from '../autogen/bundle.js';

import saveAs from 'file-saver';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'PlayerInfo',

  components: {
    LoadingIndicator,
    QuestProtoNameList,
    ClubLink,
  },

  props: {
    playerId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,

      /** @type {?PlayerInfo} */
      info: null,

      nowSec: Date.now() / 1000,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {boolean} */
    canShowDeletedByRequestView() {
      if(!this.isLoadedNormal) {
        return false;
      }

      const info = this.info;
      if(info === null) {
        return false;
      }

      return isPlayerDeletedByRequest(info.status);
    },

    /** @returns {boolean} */
    canShowDeletedByInsiderUserView() {
      if(!this.isLoadedNormal) {
        return false;
      }

      const info = this.info;
      if(info === null) {
        return false;
      }

      return isPlayerDeletedByInsiderUser(info.status);
    },

    /** @returns {boolean} */
    canShowExistingPlayerView() {
      if(!this.isLoadedNormal) {
        return false;
      }

      const info = this.info;
      if(info === null) {
        return false;
      }

      return isPlayerExist(info.status);
    },

    /** @returns {Object} */
    name() {
      return getPlayerName(this.info);
    },

    /** @returns {boolean} */
    hasName() {
      return playerHasName(this.info);
    },

    /** @returns {Object} */
    getNameClass() {
      return {
        'not-specified': !this.hasName,
      };
    },

    /** @returns {boolean} */
    mayManage() {
      return mayManage(this.actor.role);
    },

    /** @returns {Object} */
    mayEditPlayerLevel() {
      return doMayEditPlayerLevel(this.actor.role);
    },
  
      /** @returns {Object} */
    isBanned() {
      const banData = this.info.banData;
      if(banData === null) {
        return false;
      }

      if(!banData.isBanned) {
        return false;
      }
      if(banData.ftime === 0) {
        // Player was banned permanently.
        return true;
      }

      // Player was banned temporarily.
      return this.nowSec < banData.ftime;
    },

    /** @returns {Object} */
    banStr() {
      const banData = this.info.banData;
      if(banData === null) {
        return 'no';
      }

      if(!banData.isBanned) {
        return 'no';
      }

      if(banData.ftime === 0) {
        return 'Player was banned permanently.';
      }
      if(this.nowSec < banData.ftime) {
        const banEndStr = getMomentMinStr(banData.ftime);
        return 'Player is banned until ' + banEndStr + '.';
      }
      return 'no (ban ended)';
    },

    /** @returns {Object} */
    banClass() {
      return {
        banned: this.isBanned,
      };
    },

    /** @returns {Object} */
    clubChatStatusStr() {
      if(this.info.chatStatusInfo.blocked) {
        const blockEndStr = getMomentMinStr(this.info.chatStatusInfo.blockEnd);
        return 'Club chat is blocked unitl ' + blockEndStr + '.';
      }
      return 'available';
    },

    /** @returns {string} */
    playerSuspiciousActivityStr() {
      return getPlayerSuspiciousActivityStr(this.info.suspicionsMask);
    },

    /** @returns {Object} */
    clubChatStatusClass() {
      return {
        chatBlocked: this.info.chatStatusInfo.blocked,
      };
    },

    /** @returns {Object} */
    suspiciousActivityClass() {
      const suspicionsCount = this.info.suspicionsCount;
      return {
        suspicionHigh:     suspicionsCount >= 3,
        suspicionModerate: suspicionsCount == 2,
        suspicionLow:      suspicionsCount == 1,
      };
    },

    /** @returns {string} */
    eventAccessStatusStr() {
      return getEventAccessStatusName(this.info.eventAccessStatus);
    },

    /** @returns {Object} */
    eventAccessClass() {
      return getEventAccessStatusClass(this.info.eventAccessStatus);
    },

    activeQuestProtoIds() {
      if(!this.isLoadedNormal) {
        return null;
      }

      const info = this.info;
      if(info === null) {
        return null;
      }

      return info.activeQuestProtoIds;
    },

    /** @returns {boolean} */
    hasActiveQuests() {
      const activeQuestProtoIds = this.activeQuestProtoIds;
      if(activeQuestProtoIds === null) {
        return false;
      }

      return activeQuestProtoIds.length > 0;
    },
  },

  created() {
    notifier.deleteSenderNotifications(this);

    this.loadState = LOAD_STATE_LOADING;

    HTTP.get('/player/' + this.playerId + '/info')
      .then(response => {
        this.info = response.data.data;
        this.loadState = LOAD_STATE_LOADED_NORMAL;
     })
    .catch(err => {
      this.loadState = LOAD_STATE_LOADED_ERROR;
      notifier.showError(err, {sender: this});
    });
  },

  methods: {
    downloadState() {
      HTTP.get('/player/' + this.playerId + '/state', {responseType: 'blob'})
      .then(response => {
        saveAs(new Blob([response.data]), 'player_state_' + this.playerId + '.dat');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      })
    },
  },

  filters: {
    /**
     * @param {string} advertisingId
     * @returns {string}
     */
    getAdvertisingIdStr(advertisingId) {
      if(advertisingId === null || advertisingId.length === 0) {
        return 'unknown';
      }
      return advertisingId;
    },

    getEnumPlayerRFMCategoryName,
    getEnumTmntIndividualLeagueName,
    getPlayerSuspiciousActivityStr,
  },
};
</script>

<style scoped>
.active-quests > th,
.active-quests > td {
  padding-bottom: 1.5rem;
}

.chatBlocked {
  color: var(--red);
}
.banned {
  color: var(--red);
}
.suspicionHigh {
  color: var(--red);
}
.suspicionModerate {
  color: var(--orange);
}
.suspicionLow {
  color: var(--yellow);
}
</style>
